import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * VkSubscribeWidget
 * Embeds VK Community widgets based on official VK Open API documentation:
 * - Community Widget: VK.Widgets.Group (https://dev.vk.ru/ru/widgets/community) -> Shows community logo, title, followers, and subscribe button
 * - Subscribe Widget: VK.Widgets.Subscribe (https://dev.vk.ru/ru/widgets/subscribe) -> Simple author/group follow button
 *
 * @param {Object} props
 * @param {string} [props.id] Optional base container DOM ID
 * @param {string|number} [props.groupId] VK Community ID (defaults to REACT_APP_VK_GROUP_ID || 235508257)
 * @param {string|number} [props.appId] VK App ID (defaults to REACT_APP_VK_APP_ID || 54432094)
 * @param {'group'|'subscribe'} [props.widgetType='group'] 'group' for official Community card (with logo & followers), 'subscribe' for follow button
 * @param {number} [props.mode=3] For group: 1 (header only), 3 (header + follower row). For subscribe: 0, 1, 2
 * @param {number} [props.soft=1] 1 for soft authorization (subscribe widget)
 * @param {number|string} [props.width='auto'] Width in px or 'auto'
 * @param {number|string} [props.height] Height in px
 * @param {boolean} [props.noCover=true] Hide large banner cover if group has one
 * @param {boolean} [props.showFallback=true] Whether to show direct link button if OpenAPI script fails to load
 * @param {string} [props.className=''] Optional wrapper class names
 */
export default function VkSubscribeWidget({
    id,
    groupId = process.env.REACT_APP_VK_GROUP_ID || '235508257',
    appId = process.env.REACT_APP_VK_APP_ID || '54432094',
    widgetType = 'group',
    mode = 3,
    soft = 1,
    width = 'auto',
    height,
    color1,
    color2,
    color3,
    noCover = true,
    showFallback = true,
    className = '',
}) {
    const { t } = useTranslation();
    const containerRef = useRef(null);
    const activeSlotRef = useRef(null);
    const isLoadedRef = useRef(false);
    const [, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        let isCancelled = false;
        const scriptId = 'vk-openapi-script';

        const detachSlot = () => {
            if (activeSlotRef.current && activeSlotRef.current.parentNode) {
                activeSlotRef.current.parentNode.removeChild(activeSlotRef.current);
                activeSlotRef.current.id = '';
                activeSlotRef.current = null;
            }
        };

        const initWidget = () => {
            if (isCancelled || !containerRef.current) return;

            // Wait if the container is not yet laid out (e.g. during modal transition)
            const currentWidth = containerRef.current.offsetWidth;
            if (currentWidth === 0) {
                requestAnimationFrame(() => {
                    if (!isCancelled) setTimeout(initWidget, 50);
                });
                return;
            }

            if (window.VK && window.VK.Widgets) {
                try {
                    // Detach any previous slot so queued macro-task iframes don't append to DOM
                    detachSlot();
                    containerRef.current.innerHTML = '';

                    // Create an isolated inner slot element with a unique ID
                    const inner = document.createElement('div');
                    const slotId = id
                        ? `${id}_${Math.random().toString(36).substring(2, 8)}`
                        : `vk_slot_${Math.random().toString(36).substring(2, 9)}`;
                    inner.id = slotId;

                    let effectiveWidth = width;

                    // VK Community Widget threshold: at width < 380px, VK enables "wcommunity_nano"
                    // which hides follower profile photos. We request 380px and scale the inner slot
                    // cleanly into the available mobile container width so followers always show.
                    if (widgetType === 'group' && width === 'auto') {
                        if (currentWidth < 380) {
                            const scale = currentWidth / 380;
                            inner.style.width = '380px';
                            inner.style.transform = `scale(${scale})`;
                            inner.style.transformOrigin = 'top left';
                            containerRef.current.style.height = `${Math.round(185 * scale)}px`;
                            containerRef.current.style.overflow = 'hidden';
                            effectiveWidth = 380;
                        } else {
                            inner.style.width = '100%';
                            inner.style.transform = 'none';
                            containerRef.current.style.height = 'auto';
                            containerRef.current.style.overflow = 'visible';
                            effectiveWidth = currentWidth;
                        }
                    } else {
                        inner.style.width = '100%';
                    }

                    containerRef.current.appendChild(inner);
                    activeSlotRef.current = inner;

                    // Optional VK.init for widgets
                    if (typeof window.VK.init === 'function') {
                        try {
                            window.VK.init({
                                apiId: appId ? Number(appId) : undefined,
                                onlyWidgets: true,
                            });
                        } catch (err) {
                            // Non-fatal if already initialized
                        }
                    }

                    if (widgetType === 'group') {
                        const groupFn = window.VK.Widgets.Group || window.VK.Widgets.Community;
                        if (typeof groupFn === 'function') {
                            const groupOptions = {
                                mode: Number(mode),
                                no_cover: noCover ? 1 : 0,
                            };
                            if (effectiveWidth !== undefined) groupOptions.width = effectiveWidth;
                            if (height !== undefined) groupOptions.height = height;
                            if (color1 !== undefined) groupOptions.color1 = String(color1).replace('#', '');
                            if (color2 !== undefined) groupOptions.color2 = String(color2).replace('#', '');
                            if (color3 !== undefined) groupOptions.color3 = String(color3).replace('#', '');

                            const numericGroupId = Math.abs(Number(groupId));
                            groupFn(slotId, groupOptions, numericGroupId);

                            isLoadedRef.current = true;
                            setIsLoaded(true);
                            setHasError(false);
                        } else {
                            if (!isCancelled) setHasError(true);
                        }
                    } else {
                        // widgetType === 'subscribe'
                        if (typeof window.VK.Widgets.Subscribe === 'function') {
                            const subOptions = {
                                mode: Number(mode),
                                soft: Number(soft),
                            };
                            if (effectiveWidth !== undefined) subOptions.width = effectiveWidth;

                            const targetOid = -Math.abs(Number(groupId));
                            window.VK.Widgets.Subscribe(slotId, subOptions, targetOid);

                            isLoadedRef.current = true;
                            setIsLoaded(true);
                            setHasError(false);
                        } else {
                            if (!isCancelled) setHasError(true);
                        }
                    }
                } catch (err) {
                    console.error('[VkSubscribeWidget] Failed to initialize VK widget:', err);
                    if (!isCancelled) setHasError(true);
                }
            } else {
                if (!isCancelled) setHasError(true);
            }
        };

        let interval = null;
        let timeout = null;

        const checkAndInit = () => {
            if (window.VK && window.VK.Widgets) {
                if (interval) {
                    clearInterval(interval);
                    interval = null;
                }
                if (timeout) {
                    clearTimeout(timeout);
                    timeout = null;
                }
                initWidget();
                return true;
            }
            return false;
        };

        if (checkAndInit()) {
            // Script is ready
        } else {
            let script = document.getElementById('vk-openapi-main') ||
                document.getElementById(scriptId) ||
                document.querySelector('script[src*="openapi.js"]');

            if (!script) {
                script = document.createElement('script');
                script.id = scriptId;
                script.src = 'https://vk.com/js/api/openapi.js?169';
                script.async = true;

                script.onload = () => {
                    if (!isCancelled) checkAndInit();
                };

                script.onerror = () => {
                    console.warn('[VkSubscribeWidget] Failed to load VK OpenAPI script from https://vk.com');
                    if (!isCancelled) setHasError(true);
                };

                document.head.appendChild(script);
            } else {
                script.addEventListener('load', checkAndInit, { once: true });
            }

            // Polling fallback
            interval = setInterval(checkAndInit, 100);

            // Generous timeout
            timeout = setTimeout(() => {
                if (interval) clearInterval(interval);
                if (!isLoadedRef.current && !isCancelled) {
                    setHasError(true);
                }
            }, 12000);
        }

        return () => {
            isCancelled = true;
            if (interval) clearInterval(interval);
            if (timeout) clearTimeout(timeout);
            detachSlot();
        };
    }, [id, groupId, appId, widgetType, mode, soft, width, height, color1, color2, color3, noCover]);

    if (hasError && showFallback) {
        return (
            <div className={`flex flex-col items-center justify-center my-2 ${className}`}>
                <a
                    href={`https://vk.com/club${groupId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#0077FF] hover:bg-[#0066DD] text-white text-sm font-medium transition-colors shadow-xs"
                >
                    <img
                        src={`${process.env.PUBLIC_URL || ''}/images/vk-monogram.svg`}
                        alt="VK"
                        className="w-4 h-4 object-contain"
                        onError={(e) => {
                            e.currentTarget.style.display = 'none';
                        }}
                    />
                    <span>{t('pets.success_vk_subscribe_btn', { defaultValue: 'Suscribirse en VK' })}</span>
                </a>
            </div>
        );
    }

    return (
        <div className={`w-full min-h-[44px] ${className}`}>
            <div
                ref={containerRef}
                className="w-full [&>iframe]:!w-full"
            />
        </div>
    );
}
